import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Input, Popconfirm, Row, Select, SelectProps, Space, Tooltip, Modal, DatePicker } from "antd";
import { Toast } from 'primereact/toast'; // Replace react-hot-toast import
import { useTranslation } from 'react-i18next';
import { StateLoader } from '../../../redux/StateLoader';
import { IAuthentication, UserRole } from '../../../interfaces/authentication';
import i18n from '../../../i18n';
import { ICompanyData } from "../../../interfaces/ICompanyData";
import HTTPService from "../../../utils/makerequest";
import { useParams } from "react-router-dom";
import { SyncOutlined, CloseOutlined } from "@ant-design/icons";

const UpdateActivityName: React.FunctionComponent<any> = () => {
    const { activityId } = useParams<any>();
    const { t } = useTranslation();
    const [companyData, setCompanyData] = useState<ICompanyData[]>();
    const [selectedCompany, setSelectedCompany] = useState<number | undefined>(undefined);
    const loader = new StateLoader();
    const userInfo: IAuthentication = loader.loadAuthState();
    const [activityData, setActivityData] = useState<any>();
    const toast = useRef<any>(null);

    useEffect(() => {
        fetchData();
        getActivityId(activityId);
    }, []);

    const fetchData = async () => {
        const result = await HTTPService.GETCompanyNames();
        setCompanyData(result);
    };

    const updateActivity = (formData) => {
        const updateData = {
            activityId: activityId,
            activityName: formData.activityName,
            companyId: selectedCompany || userInfo?.companyId,
            maintenanceType: activityData?.maintenanceType,
        };

        HTTPService.PUT(`/Activity/updateActivity`, updateData)
            .then((res) => {
                if (res.status === 200) {
                    toast.current?.show({
                        severity: 'success',
                        summary: t('successful'),
                        detail: t('update_success'),
                        life: 3000
                    });
                    window.location.replace("/admin/activity-name");
                } else {
                    const errorMessage = typeof res.data === 'string'
                        ? res.data
                        : res.data?.message || t('update_failed');
                    toast.current?.show({
                        severity: 'error',
                        summary: t('errors'),
                        detail: errorMessage,
                        life: 3000
                    });
                }
            })
            .catch((error) => {
                console.error('Update Error:', error);
                const errorMessage = error?.response?.data?.message
                    || error?.message
                    || t('update_failed');
                toast.current?.show({
                    severity: 'error',
                    summary: t('errors'),
                    detail: errorMessage,
                    life: 3000
                });
            });
    };

    const getActivityId = (activityId: number) => {
        HTTPService.GET(`/Activity/ById?activityId=${activityId}`)
            .then((res) => {
                if (res.status === 200 && res.data) {
                    setActivityData(res.data);
                    setSelectedCompany(res.data.companyTBL.companyId);
                } else {
                    toast.current?.show({
                        severity: 'error',
                        summary: t('errors'),
                        detail: res.error?.message || t('error_fetching_data'),
                        life: 3000
                    });
                }
            })
            .catch((error) => {
                console.error("Error fetching activity data:", error);
                toast.current?.show({
                    severity: 'error',
                    summary: t('errors'),
                    detail: t('error_fetching_data'),
                    life: 3000
                });
            });
    };

    const handleCancel = () => {
        window.location.replace("/admin/activity-name/");
    };

    const filterOption: SelectProps<any>['filterOption'] = (input, option) => {
        return (option?.children as unknown as string).toLowerCase().includes(input.toLowerCase());
    };

    return (
        <Form
            layout="vertical"
            onFinish={updateActivity}
            fields={[
                { name: ["activityName"], value: activityData?.activityName },
                { name: ["companyId"], value: activityData?.companyTBL.companyId }
            ]}
        >
            <Toast ref={toast} />
            <Card style={{ padding: "50px" }}>
                <h3>{t('update_activity')}</h3>
                {userInfo?.role === UserRole.AltisAdmin && (
                    <Form.Item
                        name="companyId"
                        label={t('Select company')}
                        rules={[{ required: true, message: t('please_select_a_company') }]}
                    >
                        <Select showSearch placeholder={t('company_name')} style={{ marginBottom: '6px' }} filterOption={filterOption}
                            onChange={(value) => setSelectedCompany(value)} value={selectedCompany}>
                            {companyData?.map((company) => (
                                <Select.Option key={company.companyId} value={company.companyId}>
                                    {company.companyName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                )}

                <Form.Item
                    label={t('activity_name')}
                    name="activityName"
                    rules={[{ required: true, message: t('enter_activity_name') }]}
                >
                    <Input placeholder={t('enter_activity_name')} style={{ borderRadius: '5px' }} />
                </Form.Item>

                <Form.Item>
                    <Row gutter={[4, 4]} justify="end" align="middle">
                        <Col xs={12} sm={4}>
                            <Button
                                block
                                onClick={handleCancel}
                                type="primary"
                                style={{
                                    height: 64,
                                    backgroundColor: 'transparent',
                                    color: '#1890ff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 10px',
                                    border: 'none',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.color = '#40a9ff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.color = '#1890ff';
                                }}
                            >
                                <CloseOutlined style={{ fontSize: 20 }} />
                                <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                                    {t('cancel')}
                                </span>
                            </Button>
                        </Col>
                        <Col xs={12} sm={4}>
                            <Button
                                block
                                htmlType="submit"
                                type="primary"
                                style={{
                                    height: 64,
                                    backgroundColor: 'transparent',
                                    color: '#1890ff',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: '0 10px',
                                    border: 'none',
                                    transition: 'all 0.3s ease',
                                    cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.2)';
                                    e.currentTarget.style.color = '#40a9ff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = 'none';
                                    e.currentTarget.style.color = '#1890ff';
                                }}
                            >
                                <SyncOutlined style={{ fontSize: 20 }} />
                                <span style={{ fontSize: '12px', marginTop: '8px', textAlign: 'center' }}>
                                    {t('update')}
                                </span>
                            </Button>
                        </Col>
                    </Row>
                </Form.Item>
            </Card>
        </Form>
    );
};

export default UpdateActivityName;
